<template>
  <div
    class="signal-summary d-flex flex-column"
    :class="[compact ? 'align-items-center' : 'w-100 align-items-start']"
  >
    <div class="signal-data d-flex flex-row i-gap-6">
      <div
        v-if="!hideIcon"
        :class="[
          'signal-icon-wrapper d-flex align-items-center justify-content-center',
          { fraudulent: signal.failed },
          { legitimate: signal.failed === false },
          { circle: !compact },
        ]"
      >
        <font-awesome-icon class="signal-icon" :icon="signalConfigObj.icon" />
      </div>
      <div
        class="signal-text d-flex flex-column align-items-start justify-content-center"
      >
        <span class="signal-name">{{ title }}</span>
        <span v-if="!compact" class="signal-description">
          <span>
            <slot name="description" :signal-config="signalConfigObj">{{
              description
            }}</slot>
          </span>
          <div
            v-if="detailed && signalConfigObj.relatedMetadata && metadata"
            class="mt-1 d-flex flex-column"
          >
            <div
              v-for="property in relatedMetadata"
              :key="property"
              class="signal-detail-item my-1 d-flex"
            >
              <div>
                <strong>{{ metadataLabel(property) }}</strong>
                <metadata-value
                  :metadata-items="metadata[property]"
                  class="w-100"
                />
              </div>
            </div>
          </div>
        </span>
      </div>
    </div>
    <i-button
      v-if="stacked && signalConfigObj.ctaHandler"
      :text="signalConfigObj.getCtaText(signal)"
      class="i-ml-13"
      type="neutral"
      subtype="subtle"
      size="sm"
      @button-click="() => signalConfigObj.ctaHandler(signal, document)"
    ></i-button>
  </div>
</template>

<script>
import humanizeString from 'humanize-string';
import signalConfig from '@/constants/signalConfig';
import { displayMetadataLabel } from '@/utils/metadata';
import MetadataValue from '@/V2/documents/MetadataValue';
import { iButton } from '@inscribe/signal';

export default {
  components: { iButton, MetadataValue },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    detailed: {
      type: Boolean,
      default: false,
    },
    signal: {
      type: Object,
      default: null,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    metadata: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    signalConfigObj() {
      return (
        signalConfig[this.signal.signalName] || {
          title: humanizeString(this.signal.signalName),
          icon: 'list',
        }
      );
    },
    title() {
      const { getTitle = () => this.signalConfigObj.title } =
        this.signalConfigObj;
      return getTitle(this.signal);
    },
    description() {
      if (this.$slots.description) return null;

      const { getDescription = this.getDefaultDescription } =
        this.signalConfigObj;
      return getDescription(this.signal);
    },
    relatedMetadata() {
      return this.signalConfigObj.relatedMetadata.filter(
        (property) => !!this.metadata[property],
      );
    },
  },
  methods: {
    getDefaultDescription() {
      return this.signal.pages
        ? this.signal.pages[0].description
        : this.signal.description;
    },
    metadataLabel: displayMetadataLabel,
  },
};
</script>

<style lang="scss">
.signal-summary {
  height: 100%;

  .signal-text {
    &:hover {
      text-decoration: underline;
    }
  }

  &.compact {
    gap: 8px;
  }

  .signal-icon-wrapper {
    $size: 22px;
    background: $color-navy-blue-60;
    border-radius: $border-radius-default;
    height: $size;
    min-width: $size;
    width: $size;

    &.circle {
      height: 40px;
      min-width: 40px;
      width: 40px;
      border-radius: 50%;
    }

    &.fraudulent {
      background: $light-error-30;

      .signal-icon {
        color: $light-error-110;
      }
    }

    &.legitimate {
      background: $color-strong-cyan;

      .signal-icon {
        color: $light-neutral-10;
      }
    }

    .signal-icon {
      color: $light-error-110;
      width: $font-size-small;
    }
  }

  .signal-name {
    font-weight: 700;
    color: $light-neutral-120;
    font-size: $font-size-secondary;
    line-height: initial;
  }

  .signal-description {
    color: $light-neutral-110;
    font-size: $font-size-secondary;
    line-height: 1.3;
    display: block;

    .signal-detail-item {
      strong {
        min-width: 130px;
      }

      span {
        white-space: normal;
      }
    }
  }
}
</style>
